import { useEffect, useState } from "react";
import { Plus, Trash2, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyUser = { name: "", email: "", password: "", role: "user" };

const inputCls =
  "w-full bg-zinc-900/80 border border-zinc-700 focus:border-nx-yellow focus:outline-none rounded-xl px-3.5 py-2.5 text-sm text-white transition-colors";

const Field = ({ label, children }) => (
  <div>
    <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default function Usuarios() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyUser);
  const [showForm, setShowForm] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = () => {
    if (me?.role !== "admin") {
      setDenied(true);
      setLoading(false);
      return;
    }
    Promise.allSettled([api.get("/users"), api.get("/audit")]).then(([u, a]) => {
      if (u.status === "fulfilled") setUsers(u.value.data);
      if (a.status === "fulfilled") setAuditLog(a.value.data);
      if (u.status === "rejected" && u.reason?.response?.status === 403) setDenied(true);
      setLoading(false);
    });
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="users-loading">
        <div className="w-8 h-8 border-2 border-nx-yellow border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/register", form);
      setForm(emptyUser);
      setShowForm(false);
      toast.success("Usuário criado");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      setUsers((us) => us.map((u) => (u.id === id ? { ...u, role } : u)));
      toast.success("Papel atualizado");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removeUser = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      setUsers((us) => us.filter((u) => u.id !== id));
      toast.success("Usuário removido");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (denied) {
    return (
      <div className="nx-card p-10 text-center" data-testid="users-access-denied">
        <ShieldCheck className="w-8 h-8 text-nx-yellow mx-auto mb-3" />
        <p className="text-zinc-300 font-semibold">Acesso restrito a administradores</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-nx-yellow">Administração</p>
          <h1 className="font-syne text-3xl sm:text-4xl font-extrabold mt-2" data-testid="users-title">Usuários</h1>
        </div>
        <button
          onClick={() => setShowForm(true)}
          data-testid="users-add-button"
          className="inline-flex items-center gap-2 bg-nx-yellow text-nx-dark text-sm font-bold px-5 py-2.5 rounded-full hover:bg-nx-electric transition-colors"
        >
          <Plus className="w-4 h-4" /> Novo Usuário
        </button>
      </div>

      <div className="nx-card overflow-x-auto" data-testid="users-table">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-zinc-800 text-left">
              {["Nome", "E-mail", "Papel", "Ações"].map((h) => (
                <th key={h} className="px-5 py-4 text-[11px] font-mono uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-zinc-800/50 last:border-0 hover:bg-zinc-900/40" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-3.5 font-semibold">
                  {u.name}
                  {u.id === me?.id && <span className="ml-2 text-[10px] font-mono text-nx-yellow">(você)</span>}
                </td>
                <td className="px-5 py-3.5 text-zinc-400">{u.email}</td>
                <td className="px-5 py-3.5">
                  <select
                    value={u.role}
                    onChange={(e) => changeRole(u.id, e.target.value)}
                    disabled={u.id === me?.id}
                    data-testid={`user-role-select-${u.id}`}
                    className="bg-zinc-800/80 border border-zinc-700 rounded-lg px-2.5 py-1.5 text-xs text-zinc-200 focus:border-nx-yellow focus:outline-none disabled:opacity-50"
                  >
                    <option value="admin">Admin</option>
                    <option value="user">Equipe</option>
                  </select>
                </td>
                <td className="px-5 py-3.5">
                  {u.id !== me?.id && (
                    <button onClick={() => removeUser(u.id)} data-testid={`user-delete-${u.id}`} className="text-zinc-600 hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-zinc-500">Nenhum usuário encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="nx-card overflow-x-auto" data-testid="audit-log-table">
        <div className="px-5 py-4 border-b border-zinc-800">
          <h2 className="font-syne text-lg font-bold">Atividade administrativa</h2>
        </div>
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="border-b border-zinc-800 text-left">
              {["Quando", "Quem", "Ação", "Detalhe"].map((h) => (
                <th key={h} className="px-5 py-4 text-[11px] font-mono uppercase tracking-widest text-zinc-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {auditLog.map((a, i) => (
              <tr key={i} className="border-b border-zinc-800/50 last:border-0" data-testid={`audit-row-${i}`}>
                <td className="px-5 py-3 font-mono text-xs text-zinc-400 whitespace-nowrap">
                  {a.created_at ? new Date(a.created_at).toLocaleString("pt-BR") : "—"}
                </td>
                <td className="px-5 py-3 text-xs text-zinc-300">{a.actor}</td>
                <td className="px-5 py-3">
                  <span className="text-[10px] font-mono uppercase border border-nx-yellow/40 text-nx-yellow rounded-full px-2.5 py-1">{a.action}</span>
                </td>
                <td className="px-5 py-3 text-xs text-zinc-500 max-w-[280px] truncate">{a.detail}</td>
              </tr>
            ))}
            {auditLog.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-8 text-center text-zinc-500">Nenhuma atividade registrada.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="user-form-modal">
          <form onSubmit={addUser} className="glass rounded-3xl p-7 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-syne text-xl font-bold">Novo Usuário</h2>
              <button type="button" onClick={() => setShowForm(false)} data-testid="user-form-close" className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <Field label="Nome *">
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="user-name-input" />
              </Field>
              <Field label="E-mail *">
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="user-email-input" />
              </Field>
              <Field label="Senha *">
                <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="Mínimo 6 caracteres" data-testid="user-password-input" />
              </Field>
              <Field label="Papel">
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="user-role-select">
                  <option value="user">Equipe (visualiza e cria)</option>
                  <option value="admin">Admin (acesso total)</option>
                </select>
              </Field>
            </div>
            <button type="submit" data-testid="user-submit-button" className="mt-6 w-full bg-nx-yellow text-nx-dark font-bold py-3 rounded-xl hover:bg-nx-electric transition-colors">
              Criar usuário
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
